import React from "react";
import { motion } from "framer-motion";
import { StatusBadge } from "../components/Badges";
import CTA from "../components/CTA";
import { PageHeader } from "./Platform";

const timeline = [
  {
    year: "2025",
    era: "Current platform",
    accent: "emerald",
    caption: "Available today. In production with hotels and hospital groups.",
    milestones: [
      { t: "Guest App", s: "available" },
      { t: "Staff App", s: "available" },
      { t: "HOP Dashboard", s: "available" },
      { t: "Pre Check-In", s: "available" },
      { t: "Digital Room Key", s: "available" },
      { t: "PMS Integration", s: "available" },
      { t: "POS Integration", s: "available" },
      { t: "Multilingual Platform", s: "available" },
    ],
  },
  {
    year: "2026",
    era: "AI foundation",
    accent: "purple",
    caption:
      "Intelligence layered onto the platform. Guest-first, staff-first, then GM-first.",
    milestones: [
      { t: "Conversational Concierge", s: "inDevelopment" },
      { t: "Voice Requests", s: "inDevelopment" },
      { t: "AI Translation", s: "inDevelopment" },
      { t: "Smart Task Routing", s: "planned" },
      { t: "Guest Personalization", s: "planned" },
      { t: "AI Knowledge Assistant", s: "planned" },
      { t: "Executive Summaries", s: "planned" },
      { t: "Predictive Staffing", s: "planned" },
    ],
  },
  {
    year: "2027",
    era: "Autonomous operations",
    accent: "orange",
    caption:
      "The hotel that helps run itself. Predictive, self-healing, IoT-aware.",
    milestones: [
      { t: "Predictive Maintenance", s: "futureVision" },
      { t: "Computer Vision QA", s: "futureVision" },
      { t: "IoT Intelligence", s: "futureVision" },
      { t: "Revenue AI", s: "futureVision" },
      { t: "Cross-property Intelligence", s: "futureVision" },
      { t: "Autonomous Task Assignment", s: "futureVision" },
      { t: "Digital Twin Operations", s: "futureVision" },
      { t: "Natural Language Search", s: "futureVision" },
    ],
  },
];

const accentBg = {
  emerald: "bg-emerald-400",
  purple: "bg-brand-purpleLight",
  orange: "bg-brand-orange",
};

const accentText = {
  emerald: "text-emerald-300",
  purple: "text-brand-purpleLight",
  orange: "text-brand-orange",
};

export default function Roadmap() {
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="page-roadmap"
    >
      <PageHeader
        overline="Product roadmap"
        title={
          <>
            From connected today —{" "}
            <span className="grad-text">to autonomous tomorrow.</span>
          </>
        }
        subtitle="A three-year view of iNPLASS. Everything in 2026 and 2027 is under our product roadmap — not yet part of the live platform."
      />

      {/* Legend row */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 -mt-4 mb-4">
        <div
          className="flex flex-wrap items-center gap-3"
          data-testid="roadmap-legend"
        >
          <StatusBadge status="available" />
          <StatusBadge status="inDevelopment" />
          <StatusBadge status="planned" />
          <StatusBadge status="futureVision" />
        </div>
      </div>

      {/* Timeline */}
      <section className="relative py-12 md:py-20" data-testid="roadmap-timeline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-6 flex items-center justify-between">
          <div className="font-display text-[11px] uppercase tracking-[0.28em] text-white/60 font-bold">
            Scroll to explore the timeline →
          </div>
          <div className="font-display text-[11px] uppercase tracking-[0.28em] text-white/60 font-bold">
            2025 · 2026 · 2027
          </div>
        </div>

        <div className="relative overflow-x-auto hide-scrollbar">
          <div className="min-w-[1200px] px-6 md:px-10 pb-8">
            <div className="relative h-px bg-gradient-to-r from-emerald-400/80 via-brand-purpleLight/90 to-brand-orange/90 my-8 mx-2" />

            <div className="grid grid-cols-3 gap-6">
              {timeline.map((era, i) => (
                <motion.div
                  key={era.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  className="relative"
                  data-testid={`roadmap-era-${era.year}`}
                >
                  <div className="absolute -top-[46px] left-0 flex items-center gap-3">
                    <span
                      className={`w-3 h-3 rounded-full ${accentBg[era.accent]} shadow-[0_0_18px_currentColor]`}
                    />
                    <span
                      className={`font-display text-[11px] uppercase tracking-[0.28em] font-bold ${accentText[era.accent]}`}
                    >
                      {era.year}
                    </span>
                  </div>

                  <div className="rounded-2xl brand-panel p-6 md:p-7">
                    <div
                      className="font-display text-white text-[26px] md:text-[32px] leading-tight"
                      style={{ fontWeight: 800 }}
                    >
                      {era.era}
                    </div>
                    <div className="mt-2 text-white/70 text-[13.5px] max-w-md">
                      {era.caption}
                    </div>

                    <div className="mt-6 space-y-2">
                      {era.milestones.map((m) => (
                        <div
                          key={m.t}
                          className="flex items-center justify-between gap-3 rounded-lg border border-white/15 bg-white/[0.06] px-3.5 py-2.5"
                          data-testid={`milestone-${m.t.toLowerCase().replace(/\s/g, "-")}`}
                        >
                          <span className="text-white text-[13.5px] font-semibold">
                            {m.t}
                          </span>
                          <StatusBadge status={m.s} />
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTA />
    </motion.main>
  );
}
